import { observer } from 'mobx-react-lite';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { Button, Form, Icon, Segment } from 'semantic-ui-react';

import DateInput from '../../../app/common/form/DateInput';
import SelectInput from '../../../app/common/form/SelectInput';
import TextInput from '../../../app/common/form/TextInput';
import {
  formatDetailsDisp,
  formatParameterDisp,
} from '../../../app/common/util/util';
import { IOption } from '../../../app/models/option';
import { QrTicketFiltersFormValues } from '../../../app/models/qrticket';
import { RootStoreContext } from '../../../app/stores/rootStore';

interface IProps {
  salesId: string;
}

const QrTicketFilters: React.FC<IProps> = ({ salesId }) => {
  const rootStore = useContext(RootStoreContext);
  const { depositSalesTicket } = rootStore.depositSalesTicketStore;
  const {
    ticketReserveStatusFilterOptions,
    ticketEnterStatusFilterOptions,
    qrTicketFilters,
    setQrTicketFilters,
  } = rootStore.qrTicketStore;

  const [formValues, setFormValues] = useState(new QrTicketFiltersFormValues());
  const [
    ticketReserveTimeFromSelectedValue,
    setTicketReserveTimeFromSelectedValue,
  ] = useState(null);
  const [startTimeFromSelectedValue, setStartTimeFromSelectedValue] = useState(
    null
  );

  useEffect(() => {
    if (qrTicketFilters != null) {
      const { ...filters } = qrTicketFilters;
      filters.salesId = parseInt(salesId, 10);
      filters.ticketReserveTimeFrom =
        filters.ticketReserveTimeFrom &&
        new Date(filters.ticketReserveTimeFrom);
      filters.ticketReserveTimeTo =
        filters.ticketReserveTimeTo && new Date(filters.ticketReserveTimeTo);
      filters.startTimeFrom =
        filters.startTimeFrom && new Date(filters.startTimeFrom);
      filters.startTimeTo =
        filters.startTimeTo && new Date(filters.startTimeTo);
      setFormValues(new QrTicketFiltersFormValues(filters));
    }
  }, [salesId, qrTicketFilters]);

  const handleFinalFormSubmit = (values: any) => {
    const { ...filters } = values;
    filters.salesId = parseInt(salesId, 10);
    filters.ticketReserveTimeFrom =
      filters.ticketReserveTimeFrom &&
      formatParameterDisp(filters.ticketReserveTimeFrom);
    filters.ticketReserveTimeTo =
      filters.ticketReserveTimeTo &&
      formatParameterDisp(filters.ticketReserveTimeTo);
    filters.startTimeFrom =
      filters.startTimeFrom && formatParameterDisp(filters.startTimeFrom);
    filters.startTimeTo =
      filters.startTimeTo && formatParameterDisp(filters.startTimeTo);
    filters.ticketReserveStatus =
      filters.ticketReserveStatus !== ''
        ? filters.ticketReserveStatus
        : undefined;
    filters.ticketEnterStatus =
      filters.ticketEnterStatus !== '' ? filters.ticketEnterStatus : undefined;
    setQrTicketFilters(filters);
  };

  return (
    <Fragment>
      <Segment placeholder>
        <FinalForm
          initialValues={formValues}
          onSubmit={handleFinalFormSubmit}
          mutators={{
            handleReserveStatusChange: ([, data], state, { changeValue }) => {
              const { key } = data.options.find(
                (o: IOption) => o.value === data.value
              );
              changeValue(state, 'ticketReserveStatus', () => key);
            },
            handleEnterStatusChange: ([, data], state, { changeValue }) => {
              const { key } = data.options.find(
                (o: IOption) => o.value === data.value
              );
              changeValue(state, 'ticketEnterStatus', () => key);
            },
            handleTicketReserveTimeFromChange: ([event]) => {
              setTicketReserveTimeFromSelectedValue(event);
            },
            handleStartTimeChange: ([event]) => {
              setStartTimeFromSelectedValue(event);
            },
          }}
          render={({ form, handleSubmit }) => (
            <Form size='small' onSubmit={handleSubmit}>
              <Form.Group widths='16'>
                <Form.Input
                  readOnly
                  label='チケットNo'
                  value={
                    depositSalesTicket && depositSalesTicket.ticketNo !== ''
                      ? depositSalesTicket.ticketNo
                      : ''
                  }
                />
                <Form.Input
                  readOnly
                  label='委託先'
                  value={
                    depositSalesTicket && depositSalesTicket.agentName !== ''
                      ? depositSalesTicket.agentName
                      : ''
                  }
                />
                <Form.Input
                  readOnly
                  label='作成日'
                  value={
                    depositSalesTicket &&
                    depositSalesTicket.registrationDate !== undefined
                      ? formatDetailsDisp(depositSalesTicket.registrationDate)
                      : ''
                  }
                />
                <Form.Input
                  readOnly
                  label='入場可能期間（From）'
                  value={
                    depositSalesTicket &&
                    depositSalesTicket.startDate !== undefined
                      ? formatDetailsDisp(depositSalesTicket.startDate)
                      : ''
                  }
                />
                <label>～</label>
                <Form.Input
                  readOnly
                  label='（To）'
                  value={
                    depositSalesTicket &&
                    depositSalesTicket.limitMonthDate !== undefined
                      ? formatDetailsDisp(depositSalesTicket.limitMonthDate)
                      : ''
                  }
                />
              </Form.Group>
              <Form.Group widths='16'>
                <Form.Input
                  readOnly
                  label='テナント'
                  value={
                    depositSalesTicket &&
                    depositSalesTicket.mdTdTicketTypeName !== ''
                      ? depositSalesTicket.mdTdTicketTypeName
                      : ''
                  }
                />
                <Form.Input
                  readOnly
                  label='券種'
                  value={
                    depositSalesTicket &&
                    depositSalesTicket.ticketDisplayName !== ''
                      ? depositSalesTicket.ticketDisplayName
                      : ''
                  }
                />
                <Form.Input
                  readOnly
                  label='単価'
                  value={
                    depositSalesTicket && depositSalesTicket.unitPrice !== 0
                      ? depositSalesTicket.unitPrice.toLocaleString()
                      : '0'
                  }
                />
                <Form.Input
                  readOnly
                  label='発券区分'
                  value={
                    depositSalesTicket &&
                    depositSalesTicket.ticketReserveTypeName !== ''
                      ? depositSalesTicket.ticketReserveTypeName
                      : ''
                  }
                />
                <Field
                  name='ticketReserveStatusName'
                  label='ステータス'
                  options={ticketReserveStatusFilterOptions || ''}
                  value={formValues.ticketReserveStatusName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleReserveStatusChange}
                />
                <Field
                  name='ticketEnterStatusName'
                  label='入場ステータス'
                  options={ticketEnterStatusFilterOptions || ''}
                  value={formValues.ticketEnterStatusName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleEnterStatusChange}
                />
              </Form.Group>
              <Form.Group widths='16'>
                <Field
                  name='qrSeqNoFrom'
                  label='QRコード（From）'
                  placeholder='例：00001'
                  maxLength={5}
                  value={formValues.qrSeqNoFrom}
                  component={TextInput}
                />
                <label>～</label>
                <Field
                  name='qrSeqNoTo'
                  label='（To）'
                  placeholder='例：00002'
                  maxLength={5}
                  value={formValues.qrSeqNoTo}
                  component={TextInput}
                />
                <Field
                  name='ticketReserveTimeFrom'
                  label='発券日（From）'
                  value={formValues.ticketReserveTimeFrom}
                  component={DateInput}
                  inputOnChange={
                    form.mutators.handleTicketReserveTimeFromChange
                  }
                />
                <label>～</label>
                <Field
                  name='ticketReserveTimeTo'
                  label='（To）'
                  value={formValues.ticketReserveTimeTo}
                  component={DateInput}
                  minDate={ticketReserveTimeFromSelectedValue!}
                />
                <Field
                  name='startTimeFrom'
                  label='入場日（From）'
                  value={formValues.startTimeFrom}
                  component={DateInput}
                  inputOnChange={form.mutators.handleStartTimeChange}
                />
                <label>～</label>
                <Field
                  name='startTimeTo'
                  label='（To）'
                  value={formValues.startTimeTo}
                  component={DateInput}
                  minDate={startTimeFromSelectedValue!}
                />
              </Form.Group>
              <Button
                style={{ marginTop: '25px' }}
                primary
                fluid
                type='submit'
                icon
                labelPosition='right'
              >
                検索
                <Icon name='search' />
              </Button>
            </Form>
          )}
        />
      </Segment>
    </Fragment>
  );
};

export default observer(QrTicketFilters);
